import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Check } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR2 } from "@/lib/format";
import { CARD, Label } from "@/components/common";
import { ShowMore } from "@/components/ShowMore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const download = (url, name) => api.get(url, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}).catch((e) => toast.error(apiErr(e)));

function Row({ i, onSaved }) {
  const [v, setV] = useState(i.reason);
  const dirty = v.trim() !== (i.reason || "");
  const id = i.line_ids[0];
  const save = () => api.put(`/tiers-payant/ecarts/${i.statement_id}`, { line_ids: i.line_ids, reason: v })
    .then(() => { toast.success("Raison enregistrée"); onSaved(); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="grid gap-2 px-5 py-3 md:grid-cols-[1fr_1fr] md:items-center" data-testid={`ecart-row-${id}`}>
      <div className="min-w-0">
        <p className="truncate font-semibold">{i.patient} <span className="text-xs font-normal text-gray-400">· {i.payer}</span></p>
        <p className="text-xs text-gray-600">Reçu <b>{fmtEUR2(i.amount)}</b> <span className="text-gray-400">= {i.received_detail}</span></p>
        <p className="text-xs text-gray-600">Attendu <b>{i.expected != null ? fmtEUR2(i.expected) : "—"}</b> <span className="text-gray-400">= {i.expected_detail}</span></p>
        {i.diff != null && <p data-testid={`ecart-diff-${id}`} className={`text-xs font-semibold ${i.diff < 0 ? "text-rose-700" : "text-amber-700"}`}>Écart {i.diff > 0 ? "+" : ""}{fmtEUR2(i.diff)} {i.diff < 0 ? "(moins que prévu)" : "(plus que prévu)"}</p>}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`ecart-reason-${id}`} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
          placeholder="Raison de l'écart…" className="h-9 rounded-full" />
        <Button size="sm" disabled={!dirty} onClick={save} data-testid={`ecart-save-${id}`} className="rounded-full bg-[#065F46] hover:bg-[#054d39]"><Check className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function Ecarts({ refreshKey }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/tiers-payant/ecarts").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const stamp = new Date().toISOString().slice(0, 10);
  const todo = items.filter((i) => !i.reason).length;
  return (
    <div data-testid="tp-ecarts">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div><Label>Écarts de règlement ({items.length} patient{items.length > 1 ? "s" : ""} · {todo} sans raison)</Label>
          <p className="text-xs text-gray-400">Pour chaque patient : total reçu sur le relevé comparé aux versements attendus. Éditez le fichier pour les assistantes, ou notez la raison ici.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={!items.length} onClick={() => download("/tiers-payant/ecarts/xlsx", `ecarts_tiers_payant_${stamp}.xlsx`)} data-testid="ecarts-xlsx" className="rounded-full"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
          <Button size="sm" variant="outline" disabled={!items.length} onClick={() => download("/tiers-payant/ecarts/pdf", `ecarts_tiers_payant_${stamp}.pdf`)} data-testid="ecarts-pdf" className="rounded-full"><FileDown className="mr-1.5 h-4 w-4" />PDF à imprimer</Button>
        </div>
      </div>
      <div className={`${CARD} divide-y divide-gray-100`}>
        {items.length ? <ShowMore items={items} testid="ecarts-list" initial={8} render={(i) => <Row key={`${i.line_ids[0]}-${i.reason}`} i={i} onSaved={load} />} />
          : <p className="p-8 text-center text-sm text-gray-400" data-testid="ecarts-empty">Aucun écart de règlement.</p>}
      </div>
    </div>
  );
}
